/**
 * IAActivitySelectorModal Component
 * Modal for selecting IA activities to add
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  CheckCircle2, 
  Circle,
  Search,
  Info
} from 'lucide-react';
import { IASubcategoryFilter } from './IASubcategoryFilter';
import { 
  getIAActivities, 
  type IAActivity,
  type IAActivitiesResponse
} from '@/lib/ia-calculator-utils';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import { 
  getSubcategoryIdFromActivityId, 
  getSubcategoryName
} from '@/lib/ia-data-utils';

interface IAActivitySelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  practiceId: string;
  selectedActivityIds: string[];
  onSelectActivity: (activityId: string) => void;
}

// Parse combined "Objective & Validation Documentation" field into separate sections
function parseValidationCriteria(combinedText: string | undefined) {
  if (!combinedText) return { objective: '', validationDoc: '' };
  
  // The format is "Objective: [text]\n\nValidation Documentation: [text]"
  const objectiveMatch = combinedText.match(/^Objective:\s*([\s\S]*?)(?=\n\nValidation Documentation:|$)/i);
  const validationMatch = combinedText.match(/Validation Documentation:\s*([\s\S]*?)(?=\n\nInformation:|$)/i);
  
  return {
    objective: objectiveMatch ? objectiveMatch[1].trim() : '',
    validationDoc: validationMatch ? validationMatch[1].trim() : ''
  };
}

// Validation Criteria display component - shows full content (parent card controls expand/collapse)
function ValidationCriteriaBox({ 
  combinedText 
}: { 
  combinedText?: string;
}) {
  const { objective, validationDoc } = parseValidationCriteria(combinedText);

  if (!objective && !validationDoc) return null;

  return (
    <div className="mt-3 p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-4">
      <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
        <Info className="w-4 h-4" />
        CMS Validation Criteria
      </div>
      
      {/* Objective Section */}
      {objective && (
        <div className="space-y-1.5">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Objective</div>
          <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
            {objective}
          </div>
        </div>
      )}
      
      {/* Validation Documentation Section */}
      {validationDoc && (
        <div className="space-y-1.5">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Validation Documentation</div>
          <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
            {validationDoc}
          </div>
        </div>
      )}
    </div>
  );
}

export function IAActivitySelectorModal({
  open,
  onOpenChange,
  practiceId,
  selectedActivityIds,
  onSelectActivity,
}: IAActivitySelectorModalProps) {
  const { programYear } = useProgramYear();
  const [isLoading, setIsLoading] = useState(true);
  const [activitiesData, setActivitiesData] = useState<IAActivitiesResponse | null>(null);
  const [subcategory, setSubcategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedActivityId, setExpandedActivityId] = useState<string | null>(null);

  // Load activities when modal opens
  useEffect(() => {
    if (open) {
      loadActivities();
    }
  }, [open, practiceId, programYear]);

  const loadActivities = async () => {
    setIsLoading(true);
    try {
      const data = await getIAActivities(practiceId, { year: programYear });
      setActivitiesData(data);
    } catch (error) {
      console.error('Error loading activities:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter activities based on subcategory and search
  const filteredActivities = useMemo(() => {
    if (!activitiesData?.activities) return [];
    
    let filtered = activitiesData.activities;
    
    // Filter by subcategory
    if (subcategory && subcategory !== 'all') {
      filtered = filtered.filter(a => {
        const actSubcat = getSubcategoryIdFromActivityId(a.measureId);
        return actSubcat === subcategory;
      });
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a => 
        a.measureId.toLowerCase().includes(query) ||
        a.title.toLowerCase().includes(query) ||
        (a.description && a.description.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  }, [activitiesData, subcategory, searchQuery]);

  // Group activities by subcategory for display
  const groupedActivities = useMemo(() => {
    const groups: Record<string, IAActivity[]> = {};
    
    filteredActivities.forEach(activity => {
      const subcatId = getSubcategoryIdFromActivityId(activity.measureId) || 'other';
      if (!groups[subcatId]) {
        groups[subcatId] = [];
      }
      groups[subcatId].push(activity);
    });
    
    return groups;
  }, [filteredActivities]);

  const handleSelectActivity = (activityId: string) => {
    onSelectActivity(activityId);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Add Improvement Activity</DialogTitle>
          <DialogDescription>
            Select an improvement activity to add to your attestation list.
          </DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="py-2">
          <IASubcategoryFilter
            selectedSubcategory={subcategory}
            onSubcategoryChange={setSubcategory}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedCount={selectedActivityIds.length}
            totalCount={activitiesData?.total || 0}
          />
        </div>

        {/* Activity List */}
        <ScrollArea className="h-[400px] -mx-6 px-6">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : filteredActivities.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No activities found matching your criteria.</p>
              {searchQuery && (
                <Button 
                  variant="link" 
                  onClick={() => { setSearchQuery(''); setSubcategory('all'); }}
                >
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-6 pb-4">
              {Object.entries(groupedActivities).map(([subcatId, activities]) => (
                <div key={subcatId}>
                  {/* Subcategory Header */}
                  {subcategory === 'all' && (
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-sm font-medium text-gray-700">
                        {getSubcategoryName(subcatId)}
                      </span>
                      <span className="text-xs text-gray-400">
                        ({activities.length})
                      </span>
                    </div>
                  )}

                  {/* Activity Cards */}
                  <div className="space-y-2">
                    {activities.map((activity) => {
                      const isSelected = selectedActivityIds.includes(activity.measureId);
                      const isExpanded = expandedActivityId === activity.measureId;
                      
                      return (
                        <div
                          key={activity.measureId}
                          className={`
                            border rounded-lg p-4 transition-all
                            ${isSelected 
                              ? 'border-emerald-300 bg-emerald-50' 
                              : 'border-gray-200 hover:border-gray-300 hover:bg-slate-50'
                            }
                          `}
                        >
                          <div className="flex items-start justify-between gap-4">
                            {/* Activity Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold text-gray-900">
                                  {activity.measureId}
                                </span>
                                {isSelected && (
                                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Selected
                                  </Badge>
                                )}
                              </div>
                              
                              <h4 className="text-sm font-medium text-gray-800 mb-1">
                                {activity.title}
                              </h4>
                              
                              <p className={`text-sm text-gray-600 ${isExpanded ? '' : 'line-clamp-2'}`}>
                                {activity.description}
                              </p>

                              {/* Show more button (when collapsed) */}
                              {!isExpanded && activity.description && activity.description.length > 150 && (
                                <button
                                  className="text-xs text-primary hover:underline mt-1"
                                  onClick={() => setExpandedActivityId(activity.measureId)}
                                >
                                  Show more
                                </button>
                              )}

                              {/* Validation Criteria (when expanded) */}
                              {activity.validationCriteria && isExpanded && (
                                <ValidationCriteriaBox
                                  combinedText={activity.validationCriteria.validationDocumentation}
                                />
                              )}

                              {/* Show less button at bottom (when expanded) */}
                              {isExpanded && (
                                <button
                                  className="text-xs text-primary hover:underline mt-3 block"
                                  onClick={() => setExpandedActivityId(null)}
                                >
                                  Show less
                                </button>
                              )}
                            </div>

                            {/* Action Button */}
                            <div className="flex-shrink-0">
                              {isSelected ? (
                                <div className="flex items-center gap-2 text-emerald-600">
                                  <CheckCircle2 className="w-5 h-5" />
                                  <span className="text-sm font-medium">Added</span>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  onClick={() => handleSelectActivity(activity.measureId)}
                                >
                                  Add
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t">
          <p className="text-sm text-gray-500">
            {selectedActivityIds.length} {selectedActivityIds.length === 1 ? 'activity' : 'activities'} selected
          </p>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}



